import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { Confirm } from '../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import signalRService from '../../../utilities/WS/SignalRService';
import {
    GetAdminConfigBL,
    GetAdminStatsBL,
    GetAdminParticipantsBL,
    ToggleActivePersonalRouletteBL,
    ResetDataPersonalRouletteBL,
    GetExportDatasetBL,
    exportPersonalRouletteExcel,
    formatEventDate,
    SetQueueIndexBL,
    UpdatePrizeQueueBL,
    ToggleVipBoostBL
} from '../../../utilities/BL/PersonalRoulette';
import { PersonalRouletteConfigModal } from './PersonalRouletteConfigModal';
import { PersonalRouletteInstructionsModal } from './PersonalRouletteInstructionsModal';
import { PersonalRouletteRedeemModal } from './PersonalRouletteRedeemModal';
import { PersonalRouletteResetModal } from './PersonalRouletteResetModal';
import { PersonalRouletteQueueModal } from './PersonalRouletteQueueModal';

/**
 * CONSOLA COMPLETA DEL OPERADOR (TILE 41): RULETA BAJA CUP (PersonalRouletteBoard)
 * Tablero administrativo con embudo horizontal, KPIs de stock, control de registros,
 * zona peligrosa de reinicio, tabla paginada y exportación estructurada a Excel (HU-04).
 */
export const PersonalRouletteBoard = ({ getOutOfTheDynamic, roomId = null, room = null }) => {
    const { showMessage } = useFloatMessage();

    // 1. Estados de datos de participantes
    const [participants, setParticipants] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    // 2. Configuración de la sala y modales
    const [config, setConfig] = useState(null);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
    const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
    const [initialRedeemCode, setInitialRedeemCode] = useState('');
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [selectedParticipantForReset, setSelectedParticipantForReset] = useState(null);
    const [isQueueModalOpen, setIsQueueModalOpen] = useState(false);
    const [isJumpingQueue, setIsJumpingQueue] = useState(false);
    const [isTogglingVip, setIsTogglingVip] = useState(false);

    // 3. Métricas y estadísticas del embudo (HU-04)
    const [funnelStats, setFunnelStats] = useState({
        qrScan: 0,
        pageView: 0,
        formStart: 0,
        formComplete: 0,
        spinStart: 0,
        spinComplete: 0,
        voucherDownload: 0,
        prizeRedeemed: 0,
        conversionRates: {
            scanToPage: 0,
            pageToFormStart: 0,
            formToSpin: 0,
            winnerToRedeem: 0
        },
        sourceBreakdown: {}
    });
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [lastStatsUpdated, setLastStatsUpdated] = useState(null);

    // 4. Filtros, búsqueda y paginación
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, WINNERS, NON_WINNERS, REDEEMED, PENDING_REDEEM, ARCHIVED
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);

    // 5. Ordenamiento interactivo de columnas
    const [sortField, setSortField] = useState(null); // 'passCode', 'fullName', 'prize', 'status', 'createdAt', 'downloads'
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection(field === 'fullName' || field === 'passCode' ? 'asc' : 'desc');
        }
    };

    const sortedParticipants = useMemo(() => {
        if (!sortField) return participants;

        return [...participants].sort((a, b) => {
            let valA, valB;
            switch (sortField) {
                case 'passCode':
                    valA = (a.passCode || a.participationTicket || '').toLowerCase();
                    valB = (b.passCode || b.participationTicket || '').toLowerCase();
                    return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'fullName':
                    valA = (a.fullName || '').toLowerCase();
                    valB = (b.fullName || '').toLowerCase();
                    return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'prize':
                    valA = (a.prizeTitleSnapshot || a.prizeText || a.prize?.textEs || '').toLowerCase();
                    valB = (b.prizeTitleSnapshot || b.prizeText || b.prize?.textEs || '').toLowerCase();
                    return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'downloads':
                    valA = a.downloadCount || (a.voucherDownloaded ? 1 : 0);
                    valB = b.downloadCount || (b.voucherDownloaded ? 1 : 0);
                    return sortDirection === 'asc' ? valA - valB : valB - valA;
                case 'status':
                    valA = (a.status || '').toLowerCase();
                    valB = (b.status || '').toLowerCase();
                    return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'createdAt':
                    valA = new Date(a.playedAt || a.createdAt || 0).getTime();
                    valB = new Date(b.playedAt || b.createdAt || 0).getTime();
                    return sortDirection === 'asc' ? valA - valB : valB - valA;
                default:
                    return 0;
            }
        });
    }, [participants, sortField, sortDirection]);

    // 6. Mapeos de Fila de Premios y KPIs de Stock Físico (HU-07)
    const prizesMap = useMemo(() => {
        const map = new Map();
        if (config?.prizes && Array.isArray(config.prizes)) {
            config.prizes.forEach(p => {
                map.set(String(p.id), p);
            });
        }
        return map;
    }, [config]);

    const winnerPrizes = useMemo(() => {
        if (!config?.prizes || !Array.isArray(config.prizes)) return [];
        return config.prizes.filter(p => p.isWinner);
    }, [config]);

    const normalizedQueue = useMemo(() => {
        const rawQueue = config?.prizeQueue || [];
        if (!Array.isArray(rawQueue) || rawQueue.length === 0) {
            return winnerPrizes.map((p, idx) => {
                const stock = Number(p.totalStock) || 0;
                const awarded = Number(p.awardedCount) || 0;
                const remaining = Math.max(0, stock - awarded);
                return {
                    index: idx,
                    prizeId: String(p.id),
                    prize: p,
                    text: p.textEs || p.textEn || 'Premio',
                    iconUrl: p.iconUrl,
                    backgroundColor: p.backgroundColor || '#dc2626',
                    stock,
                    awarded,
                    remaining,
                    isExhausted: remaining <= 0
                };
            });
        }

        return rawQueue.map((item, idx) => {
            const prizeId = typeof item === 'string' ? item : (item?.prizeId || item?.id || '');
            const prize = prizesMap.get(String(prizeId)) || (typeof item === 'object' ? item : null);
            const stock = Number(prize?.totalStock) || 0;
            const awarded = Number(prize?.awardedCount) || 0;
            const remaining = Math.max(0, stock - awarded);
            return {
                index: idx,
                prizeId: String(prizeId),
                prize,
                text: prize?.textEs || prize?.textEn || `Premio #${idx + 1}`,
                iconUrl: prize?.iconUrl,
                backgroundColor: prize?.backgroundColor || '#dc2626',
                stock,
                awarded,
                remaining,
                isExhausted: remaining <= 0
            };
        });
    }, [config, winnerPrizes, prizesMap]);

    const isPrizeQueueMode = config?.awardingMode === 'PRIZE_QUEUE';
    const currentQueueIndex = Number(config?.currentQueueIndex) || 0;
    const isVipBoostActive = Boolean(config?.isNextSpinGuaranteedWinner);
    const vipOverridePrizeId = config?.guaranteedPrizeId || '';

    // Manejador de Salto en 1 Clic en la Fila de Premios
    const handleJumpToQueueIndex = async (index, isExhausted = false) => {
        if (isExhausted) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'No es posible seleccionar un paso con stock agotado (0 u).'
            });
            return;
        }

        setIsJumpingQueue(true);
        setConfig(prev => prev ? { ...prev, currentQueueIndex: index } : prev);

        try {
            const res = await SetQueueIndexBL(roomId, index);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Puntero de la fila cambiado al Paso #${index + 1}`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al cambiar índice de la fila.'
                });
                loadData(false);
            }
        } catch {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al actualizar índice de la fila.'
            });
            loadData(false);
        } finally {
            setIsJumpingQueue(false);
        }
    };

    // Manejador del Modo VIP (Garantizar Victoria en Próximo Tiro)
    const handleToggleVipBoost = async (nextVipState) => {
        setIsTogglingVip(true);
        const targetPrizeId = nextVipState ? (vipOverridePrizeId || null) : null;

        setConfig(prev => prev ? {
            ...prev,
            isNextSpinGuaranteedWinner: nextVipState,
            guaranteedPrizeId: targetPrizeId
        } : prev);

        try {
            const res = await ToggleVipBoostBL(roomId, nextVipState, targetPrizeId);
            if (res.success) {
                showMessage({
                    type: nextVipState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO,
                    text: nextVipState
                        ? '⚡ ¡Victoria garantizada activada para el próximo jugador!'
                        : 'Victoria garantizada desactivada. Se aplicará probabilidad estándar.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al modificar Modo VIP.'
                });
                loadData(false);
            }
        } catch {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al alternar Modo VIP.'
            });
            loadData(false);
        } finally {
            setIsTogglingVip(false);
        }
    };

    const triggerToggleVipWithConfirm = (nextVipState) => {
        setConfirmConfig({
            message: nextVipState
                ? '¿Estás segur@ de que deseas otorgar la victoria garantizada al próximo aficionado que gire la ruleta?'
                : '¿Deseas desactivar la victoria garantizada del próximo tiro y volver a la probabilidad normal?',
            textYes: nextVipState ? 'Sí, dar victoria' : 'Sí, desactivar victoria',
            textNo: 'Cancelar',
            action: async () => {
                await handleToggleVipBoost(nextVipState);
                return { success: true };
            }
        });
        setShowConfirm(true);
    };

    // Manejador de selección de premio VIP forzado
    const handleVipPrizeSelect = async (prizeId) => {
        const cleanId = prizeId || null;
        setConfig(prev => prev ? {
            ...prev,
            isNextSpinGuaranteedWinner: true,
            guaranteedPrizeId: cleanId
        } : prev);

        try {
            const res = await ToggleVipBoostBL(roomId, true, cleanId);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: cleanId
                        ? 'Premio VIP forzado para el próximo tiro.'
                        : 'Se entregará el premio en turno de la fila en el próximo tiro.'
                });
            }
        } catch {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al forzar premio VIP.'
            });
        }
    };

    const stockKPIs = useMemo(() => {
        const prizesList = config?.prizes || [];
        let total = 0;
        let awarded = 0;

        prizesList.forEach(p => {
            if (p.isWinner) {
                total += Number(p.totalStock) || 0;
                awarded += Number(p.awardedCount) || 0;
            }
        });

        const remaining = Math.max(0, total - awarded);
        return { total, awarded, remaining };
    }, [config]);

    // 7. Secciones Desplegables
    const [isParticipationSectionOpen, setIsParticipationSectionOpen] = useState(false);
    const [isDangerZoneSectionOpen, setIsDangerZoneSectionOpen] = useState(false);

    // 8. Flujo de Confirmación y Seguridad con Contraseña
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        textYes: 'Confirmar',
        textNo: 'Cancelar',
        action: async () => ({ success: true })
    });
    const [, setConfirmData] = useState(null);

    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');
    const [passwordError, setPasswordError] = useState('');

    // =========================================================================
    // CARGA DE DATOS PRINCIPAL
    // =========================================================================
    const loadData = useCallback(async (showLoadingSpinner = true) => {
        if (showLoadingSpinner) setIsLoading(true);
        try {
            // 1. Obtener configuración administrativa
            const configRes = await GetAdminConfigBL(roomId);
            if (configRes.success && configRes.data) {
                setConfig(configRes.data);
            }

            // 2. Obtener lista de participantes paginados
            const partRes = await GetAdminParticipantsBL(
                roomId,
                currentPage,
                pageSize,
                searchTerm,
                statusFilter === 'all' ? '' : statusFilter
            );

            if (partRes.success && partRes.data) {
                const data = partRes.data;
                const items = data.items || (Array.isArray(data) ? data : []);
                setParticipants(items);
                setTotalCount(data.total !== undefined ? data.total : items.length);
                setTotalPages(data.totalPages || Math.ceil((data.total || items.length) / pageSize) || 1);
            }

            // 3. Obtener estadísticas del embudo
            const statsRes = await GetAdminStatsBL(roomId);
            if (statsRes.success && statsRes.data) {
                setFunnelStats(statsRes.data);
                setLastStatsUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
            }

            setLastUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al consultar participantes y métricas de la ruleta.'
            });
        } finally {
            if (showLoadingSpinner) setIsLoading(false);
        }
    }, [roomId, searchTerm, statusFilter, currentPage, pageSize, showMessage]);

    useEffect(() => {
        loadData(true);
    }, [loadData]);

    // =========================================================================
    // SIGNALR: SINCRONIZACIÓN EN TIEMPO REAL
    // =========================================================================
    useEffect(() => {
        const handleStatsUpdate = (data) => {
            const nextStats = data?.stats || data?.data;
            if (nextStats) {
                setFunnelStats(prev => ({ ...prev, ...nextStats }));
                setLastStatsUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
            }
            if (data?.config) {
                setConfig(data.config);
            }
            loadData(false);
        };

        const handleToggleActive = (data) => {
            const nextActive = data?.isActive ?? data?.isDynamicActive ?? data?.data?.isActive ?? data?.data?.isDynamicActive;
            if (nextActive !== undefined) {
                setConfig(prev => ({ ...prev, isDynamicActive: nextActive, isActive: nextActive }));
            }
        };

        const handleConfigUpdate = (data) => {
            const nextCfg = data?.config || data?.data;
            if (nextCfg) {
                setConfig(nextCfg);
            }
            loadData(false);
        };

        const handlePrizeRedeemed = (data) => {
            const nextStats = data?.stats || data?.data;
            if (nextStats) {
                setFunnelStats(prev => ({ ...prev, ...nextStats }));
                setLastStatsUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
            }
            loadData(false);
        };

        const handleQueueUpdated = (data) => {
            const newIndex = data?.currentQueueIndex !== undefined ? data.currentQueueIndex : (data?.index ?? data?.data?.currentQueueIndex);
            const newQueue = data?.prizeQueue || data?.queue || data?.data?.prizeQueue;
            setConfig(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    ...(newIndex !== undefined ? { currentQueueIndex: Number(newIndex) } : {}),
                    ...(newQueue ? { prizeQueue: newQueue } : {})
                };
            });
        };

        const handleVipBoostUpdated = (data) => {
            const isGuaranteed = data?.data?.isNextSpinGuaranteedWinner ?? data?.isNextSpinGuaranteedWinner ?? data?.data?.isGuaranteed ?? data?.isGuaranteed ?? data?.data?.isVipActive ?? data?.isVipActive;
            const prizeId = data?.data?.guaranteedPrizeId ?? data?.guaranteedPrizeId ?? data?.data?.overridePrizeId ?? data?.overridePrizeId;
            setConfig(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    ...(isGuaranteed !== undefined ? { isNextSpinGuaranteedWinner: Boolean(isGuaranteed) } : {}),
                    ...(prizeId !== undefined ? { guaranteedPrizeId: prizeId } : {})
                };
            });
        };

        signalRService.on('personalRoulette:statsUpdate', handleStatsUpdate);
        signalRService.on('personalRoulette:toggleActive', handleToggleActive);
        signalRService.on('personalRoulette:configUpdate', handleConfigUpdate);
        signalRService.on('personalRoulette:spinUpdate', handleStatsUpdate);
        signalRService.on('personalRoulette:prizeRedeemed', handlePrizeRedeemed);
        signalRService.on('personalRoulette:queueUpdated', handleQueueUpdated);
        signalRService.on('personalRoulette:vipBoostUpdated', handleVipBoostUpdated);

        return () => {
            signalRService.off('personalRoulette:statsUpdate', handleStatsUpdate);
            signalRService.off('personalRoulette:toggleActive', handleToggleActive);
            signalRService.off('personalRoulette:configUpdate', handleConfigUpdate);
            signalRService.off('personalRoulette:spinUpdate', handleStatsUpdate);
            signalRService.off('personalRoulette:prizeRedeemed', handlePrizeRedeemed);
            signalRService.off('personalRoulette:queueUpdated', handleQueueUpdated);
            signalRService.off('personalRoulette:vipBoostUpdated', handleVipBoostUpdated);
        };
    }, [loadData]);

    // =========================================================================
    // EXPORTACIÓN A EXCEL (3 HOJAS ESTRUCTURADAS)
    // =========================================================================
    const handleExportExcel = async () => {
        setIsExporting(true);
        try {
            // Obtener dataset completo de exportación o fallback
            const datasetRes = await GetExportDatasetBL(roomId);
            let dataset = {};

            if (datasetRes.success && datasetRes.data) {
                dataset = datasetRes.data;
            } else {
                // Fallback con datos locales
                const fullParticipantsRes = await GetAdminParticipantsBL(roomId, 1, 10000, '', 'all');
                dataset = {
                    participants: fullParticipantsRes.success && fullParticipantsRes.data?.items ? fullParticipantsRes.data.items : participants,
                    stats: funnelStats,
                    prizes: config?.prizes || [],
                    config
                };
            }

            const roomName = room?.nombreSala || config?.titleEs || 'Ruleta_BajaCup';
            const res = exportPersonalRouletteExcel(dataset, roomName);

            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Reporte de Excel generado y descargado exitosamente (3 hojas)!'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al exportar archivo Excel.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al exportar la hoja de cálculo.'
            });
        } finally {
            setIsExporting(false);
        }
    };

    // =========================================================================
    // CONTROL DE REGISTROS (PAUSAR / ABRIR)
    // =========================================================================
    const triggerToggleActive = (nextState) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas ${nextState ? 'ABRIR' : 'PAUSAR'} el registro y participación en la Ruleta Baja Cup?`,
            textYes: nextState ? 'Sí, abrir ruleta' : 'Sí, pausar ruleta',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando estado de la ruleta...' });
                const res = await ToggleActivePersonalRouletteBL(nextState, roomId);
                if (res.success) {
                    setConfig(prev => ({ ...prev, isDynamicActive: nextState, isActive: nextState }));
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `Ruleta ${nextState ? 'activada (registros abiertos)' : 'pausada (registros cerrados)'} con éxito.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar estado.' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    // =========================================================================
    // ZONA PELIGROSA: REINICIO TOTAL DE SALA
    // =========================================================================
    const triggerResetDynamicData = () => {
        setConfirmConfig({
            message: '¿Estás segur@ de que deseas reiniciar la Ruleta Baja Cup por completo? Esto eliminará de forma irreversible a todos los participantes registrados, folios, cupones generados y telemetría de esta sala.',
            textYes: 'Sí, reiniciar ruleta',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando datos de la ruleta...' });
                const effectiveRoomId = roomId || room?.id || null;
                const res = await ResetDataPersonalRouletteBL({ roomId: effectiveRoomId, password: 'evitarborrarregistros456852' });
                if (res.success) {
                    setParticipants([]);
                    setTotalCount(0);
                    setFunnelStats({
                        qrScan: 0,
                        pageView: 0,
                        formStart: 0,
                        formComplete: 0,
                        spinStart: 0,
                        spinComplete: 0,
                        voucherDownload: 0,
                        prizeRedeemed: 0,
                        conversionRates: { scanToPage: 0, pageToFormStart: 0, formToSpin: 0, winnerToRedeem: 0 },
                        sourceBreakdown: {}
                    });
                    setLastUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: '¡Los datos de la Ruleta han sido reiniciados por completo!'
                    });
                    loadData(false);
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al reiniciar la ruleta.' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const handleStartResetFlow = () => {
        setPasswordInput('');
        setPasswordError('');
        setIsPasswordModalOpen(true);
    };

    const handleValidatePasswordSubmit = (e) => {
        if (e) e.preventDefault();
        if (passwordInput === 'evitarborrarregistros456852') {
            setIsPasswordModalOpen(false);
            setPasswordInput('');
            setPasswordError('');
            triggerResetDynamicData();
        } else {
            setPasswordError('Contraseña incorrecta. No tienes autorización para reiniciar los registros.');
        }
    };

    const roomDisplayName = room?.nombreSala || config?.titleEs || 'Ruleta Baja Cup';
    const isDynamicCurrentlyActive = config?.isDynamicActive !== false && config?.isActive !== false;

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            <div className="container-fluid px-3 py-2 text-dark PersonalRouletteBoard">
                {/* 1. ENCABEZADO PRINCIPAL */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white shadow-sm"
                            style={{ width: '52px', height: '52px', backgroundColor: config?.primaryColor || '#dc2626' }}
                        >
                            <i className="bi bi-pie-chart-fill fs-3 text-warning"></i>
                        </div>
                        <div>
                            <div className="d-flex align-items-center gap-2">
                                <h2 className="h4 fw-bold mb-0 text-dark">{roomDisplayName}</h2>
                                {/* <span className={`badge ${isDynamicCurrentlyActive ? 'bg-success-subtle text-success border border-success' : 'bg-danger-subtle text-danger border border-danger'}`}>
                                    {isDynamicCurrentlyActive ? '● Dinámica Activa' : '■ Dinámica Pausada'}
                                </span> */}
                            </div>
                            {/* <small className="text-secondary">
                                Consola de Operador • Monitoreo de embudo, control de folios y stock de premios
                                {lastUpdated && <span className="ms-2 badge bg-light text-muted border">Actualizado: {lastUpdated}</span>}
                            </small> */}
                        </div>
                    </div>

                    {/* Botones de Cabecera */}
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-danger fw-semibold d-flex align-items-center gap-2 shadow-sm"
                            onClick={() => setIsConfigModalOpen(true)}
                        >
                            <i className="bi bi-gear-fill"></i>
                            <span>Configuración</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-dark fw-semibold d-flex align-items-center gap-2 shadow-sm"
                            onClick={() => setIsInstructionsModalOpen(true)}
                            title="Ver código QR e instrucciones para el aficionado"
                        >
                            <i className="bi bi-qr-code"></i>
                            <span>Instrucciones & QR</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-warning text-dark fw-semibold d-flex align-items-center gap-2 shadow-sm"
                            onClick={() => {
                                setInitialRedeemCode('');
                                setIsRedeemModalOpen(true);
                            }}
                            title="Módulo de Canje y Entrega de Premios en Sitio (Escáner QR / Búsqueda)"
                        >
                            <i className="bi bi-gift-fill text-danger"></i>
                            <span>Canje de Premios</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center gap-1 shadow-sm"
                            onClick={() => loadData(false)}
                            disabled={isLoading}
                            title="Actualizar datos en vivo"
                        >
                            <i className={`bi bi-arrow-clockwise ${isLoading ? 'spin-icon' : ''}`}></i>
                            <span>Actualizar</span>
                        </button>
                    </div>
                </div>

                {/* 2. TARJETA MODO VIP (CONTROL EN VIVO / 1 TIRO) (HU-07) */}
                {/* <div className={`card shadow-sm border-0 rounded-4 p-3 mb-4 transition-all VipBoostCard ${
                    isVipBoostActive ? 'VipBoostCard--Active bg-dark text-white border-warning' : 'bg-white'
                }`}>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className={`rounded-3 d-flex align-items-center justify-content-center shadow-sm ${
                                    isVipBoostActive ? 'bg-warning text-dark' : 'bg-light text-secondary'
                                }`}
                                style={{ width: '48px', height: '48px' }}
                            >
                                <i className={`bi ${isVipBoostActive ? 'bi-star-fill fs-4 text-dark' : 'bi-star fs-4 text-warning'}`}></i>
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <h6 className={`mb-0 fw-bold ${isVipBoostActive ? 'text-warning' : 'text-dark'}`}>
                                        ¿Quieres que gane el sig. jugador?
                                    </h6>
                                    {isVipBoostActive && (
                                        <span className="badge bg-warning text-dark fw-bold vip-active-pulse">
                                            ⚡ VICTORIA ACTIVA (1 Tiro)
                                        </span>
                                    )}
                                </div>
                                <small className={isVipBoostActive ? 'text-white-50' : 'text-secondary'}>
                                    Fuerza que el participante que gire a continuación gane de forma inmediata (consumo de 1 uso)
                                </small>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            {/* Dropdown para forzar premio específico cuando el Modo VIP está activo /}
                            {isVipBoostActive && (
                                <div className="d-flex align-items-center gap-2">
                                    <label className="small text-white-50 text-nowrap fw-semibold">Premio forzado:</label>
                                    <select
                                        className="form-select form-select-sm bg-dark text-white border-warning"
                                        style={{ minWidth: '220px' }}
                                        value={vipOverridePrizeId}
                                        onChange={(e) => handleVipPrizeSelect(e.target.value)}
                                    >
                                        <option value="">🎯 El premio en turno (Fila / Siguiente)</option>
                                        {winnerPrizes.map((p) => {
                                            const stock = Number(p.totalStock) || 0;
                                            const awarded = Number(p.awardedCount) || 0;
                                            const remaining = Math.max(0, stock - awarded);
                                            const isExhausted = remaining <= 0;
                                            return (
                                                <option key={p.id} value={p.id} disabled={isExhausted}>
                                                    {p.textEs || p.textEn} {isExhausted ? '(❌ Agotado)' : `(🟢 ${remaining} disp.)`}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            )}

                            {/* Botón de Acción VIP /}
                            {isVipBoostActive ? (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm fw-bold d-flex align-items-center gap-2 shadow-sm"
                                    onClick={() => triggerToggleVipWithConfirm(false)}
                                    disabled={isTogglingVip}
                                >
                                    {isTogglingVip ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                        <i className="bi bi-x-circle-fill"></i>
                                    )}
                                    <span>✕ Desactivar victoria a este jugador(a)</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-warning text-dark btn-sm fw-bold d-flex align-items-center gap-2 shadow-sm"
                                    onClick={() => triggerToggleVipWithConfirm(true)}
                                    disabled={isTogglingVip}
                                >
                                    {isTogglingVip ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                        <i className="bi bi-star-fill text-dark"></i>
                                    )}
                                    <span>★ Dar victoria a este jugador(a)</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div> */}

                {/* 3. TARJETA DE FILA DE PREMIOS EN VIVO (PRIZE QUEUE STRIP) (HU-07) */}
                {isPrizeQueueMode && (
                    <div className="card shadow-sm border-0 rounded-4 p-4 bg-white mb-4 PrizeQueueStripCard">
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <div className="rounded-3 bg-danger-subtle text-danger p-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                    <i className="bi bi-list-ol fs-4"></i>
                                </div>
                                <div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <h5 className="mb-0 fw-bold text-dark">Fila de premios</h5>
                                        {/* <span className="badge bg-danger text-white">Modo Fila Activo</span> */}
                                        {/* <span className="badge bg-warning text-dark fw-bold font-monospace">
                                            Paso en Turno: #{((currentQueueIndex) % Math.max(1, normalizedQueue.length)) + 1} de {normalizedQueue.length}
                                        </span> */}
                                    </div>
                                    <small className="text-secondary">
                                        La premios se entregarán siguiente este orden (ciclo). Si quieres que uno se de en específico en sl sig. turno, solamente haz clic en el premio para activarlo.
                                    </small>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                {/* <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1.5"
                                    onClick={() => {
                                        const nextIdx = (currentQueueIndex + 1) % Math.max(1, normalizedQueue.length);
                                        const nextItem = normalizedQueue[nextIdx];
                                        handleJumpToQueueIndex(nextIdx, nextItem?.isExhausted);
                                    }}
                                    disabled={isJumpingQueue || normalizedQueue.length <= 1}
                                    title="Avanzar manualmente al siguiente paso"
                                >
                                    <i className="bi bi-skip-forward-fill text-danger"></i>
                                    <span>Avanzar +1</span>
                                </button> */}

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1.5"
                                    onClick={() => handleJumpToQueueIndex(0, normalizedQueue[0]?.isExhausted)}
                                    disabled={isJumpingQueue || currentQueueIndex === 0}
                                    title="Reiniciar puntero al Paso #1"
                                >
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                    <span>Reiniciar ciclo</span>
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger btn-sm fw-bold d-flex align-items-center gap-2 shadow-sm"
                                    onClick={() => setIsQueueModalOpen(true)}
                                    title="Abrir editor en caliente para reordenar, agregar o remover pasos"
                                >
                                    <i className="bi bi-pencil-square"></i>
                                    <span>Editar Fila</span>
                                </button>
                            </div>
                        </div>

                        {/* Tira Horizontal Interactiva de Pasos */}
                        <div
                            className="d-flex flex-nowrap overflow-auto gap-3 py-2 px-1 PrizeQueueStrip"
                            style={{
                                scrollbarWidth: 'thin',
                                WebkitOverflowScrolling: 'touch'
                            }}
                        >
                            {normalizedQueue.length === 0 ? (
                                <div className="text-center py-4 w-100 text-muted">
                                    <p className="mb-2 fw-bold">No hay pasos configurados en la fila de premios.</p>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => setIsQueueModalOpen(true)}
                                    >
                                        <i className="bi bi-plus-circle me-1"></i> Configurar Fila
                                    </button>
                                </div>
                            ) : (
                                normalizedQueue.map((item) => {
                                    const isCurrent = item.index === (currentQueueIndex % normalizedQueue.length);
                                    const isExhausted = item.isExhausted;

                                    return (
                                        <div
                                            key={item.index}
                                            style={{ flex: '0 0 205px', minWidth: '205px' }}
                                            onClick={() => !isJumpingQueue && handleJumpToQueueIndex(item.index, isExhausted)}
                                            className={`PrizeQueueStrip__Item card text-center p-3 rounded-4 shadow-sm border transition-all position-relative user-select-none ${
                                                isCurrent
                                                    ? 'PrizeQueueStrip__Item--Current border-warning border-3 shadow-lg is-current-step'
                                                    : 'border-light-subtle bg-white'
                                            } ${isExhausted ? 'PrizeQueueStrip__Item--Exhausted opacity-50 is-exhausted' : 'cursor-pointer hover-lift'}`}
                                            title={isExhausted ? 'Premio agotado' : `Clic para saltar al Paso #${item.index + 1}`}
                                        >
                                            {/* Badge Paso */}
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className={`badge ${isCurrent ? 'bg-warning text-dark fw-bold' : 'bg-dark text-white'} rounded-pill font-monospace`} style={{ fontSize: '0.72rem' }}>
                                                    Paso #{item.index + 1}
                                                </span>

                                                {isCurrent ? (
                                                    <span className="badge bg-warning text-dark border border-dark-subtle fw-bold pulse-badge badge-turno-pulse" style={{ fontSize: '0.68rem' }}>
                                                        👉 EN TURNO
                                                    </span>
                                                ) : isExhausted ? (
                                                    <span className="badge bg-danger text-white fw-bold" style={{ fontSize: '0.68rem' }}>
                                                        Agotado
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-light text-muted border" style={{ fontSize: '0.68rem' }}>
                                                        Disponible
                                                    </span>
                                                )}
                                            </div>

                                            {/* Icono del premio */}
                                            <div className="my-2 d-flex justify-content-center">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center text-white shadow-xs"
                                                    style={{
                                                        width: '46px',
                                                        height: '46px',
                                                        backgroundColor: item.backgroundColor || '#dc2626'
                                                    }}
                                                >
                                                    {item.iconUrl ? (
                                                        <img
                                                            src={item.iconUrl}
                                                            alt={item.text}
                                                            style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                                                        />
                                                    ) : (
                                                        <i className="bi bi-trophy-fill fs-5 text-warning"></i>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Nombre del Premio */}
                                            <div className="fw-bold text-dark text-truncate mb-1" title={item.text} style={{ fontSize: '0.88rem' }}>
                                                {item.text}
                                            </div>

                                            {/* Badge de Stock */}
                                            <div className="mt-auto pt-1">
                                                {isExhausted ? (
                                                    <span className="badge bg-danger-subtle text-danger border border-danger w-100 py-1" style={{ fontSize: '0.72rem' }}>
                                                        <i className="bi bi-x-circle me-1"></i>0 / {item.stock} u
                                                    </span>
                                                ) : (
                                                    <span className={`badge w-100 py-1 ${item.remaining <= 3 ? 'bg-warning-subtle text-warning-emphasis border border-warning' : 'bg-success-subtle text-success border border-success'}`} style={{ fontSize: '0.72rem' }}>
                                                        <i className="bi bi-box-seam me-1"></i>{item.remaining} / {item.stock} u
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}

                {/* 4. FILA UNIFICADA DE MÉTRICAS DEL EMBUDO EN SCROLL HORIZONTAL CONTINUO */}
                <div className="card shadow-sm border-0 rounded-4 p-4 bg-white mb-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-bar-chart-line-fill text-danger fs-5"></i>
                            <div>
                                <h5 className="mb-0 fw-bold text-dark">Estadísticas</h5>
                                <small className="text-secondary">
                                    Seguimiento desde el escaneo de QR hasta el giro de ruleta y canje de premios físicos
                                </small>
                            </div>
                        </div>

                        {lastStatsUpdated && (
                            <span className="badge bg-light text-muted border small">
                                Actualizado: {lastStatsUpdated}
                            </span>
                        )}
                    </div>

                    {/* Scroll Horizontal Continuo (overflow-x: auto, flex-wrap: nowrap) */}
                    <div
                        className="d-flex flex-nowrap overflow-auto gap-3 text-center py-2 px-1 PersonalRouletteBoard__MetricsScroll"
                        style={{
                            scrollbarWidth: 'thin',
                            WebkitOverflowScrolling: 'touch'
                        }}
                    >
                        {/* 1. Escaneos QR */}
                        <div style={{ flex: '0 0 160px', minWidth: '160px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    📱 Escaneos QR
                                </div>
                                <div className="fs-3 fw-bold text-dark">
                                    {funnelStats?.qrScanCount ?? funnelStats?.qrScan ?? 0}
                                </div>
                            </div>
                        </div>

                        {/* 2. Vistas de Página */}
                        <div style={{ flex: '0 0 160px', minWidth: '160px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    🌐 Vistas Página
                                </div>
                                <div className="fs-3 fw-bold text-primary">
                                    {funnelStats?.pageViewCount ?? funnelStats?.pageView ?? 0}
                                </div>
                            </div>
                        </div>

                        {/* 3. Formularios Iniciados */}
                        <div style={{ flex: '0 0 160px', minWidth: '160px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    ✍️ Form Inicios
                                </div>
                                <div className="fs-3 fw-bold text-info">
                                    {funnelStats?.formStartCount ?? funnelStats?.formStart ?? 0}
                                </div>
                            </div>
                        </div>

                        {/* 4. Formularios Completados */}
                        <div style={{ flex: '0 0 160px', minWidth: '160px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    ✅ Registros Listos
                                </div>
                                <div className="fs-3 fw-bold text-success">
                                    {funnelStats?.formCompleteCount ?? funnelStats?.formComplete ?? 0}
                                </div>
                            </div>
                        </div>

                        {/* 5. Giros Iniciados */}
                        <div style={{ flex: '0 0 160px', minWidth: '160px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    🎡 Giros Iniciados
                                </div>
                                <div className="fs-3 fw-bold text-warning-emphasis">
                                    {funnelStats?.spinStartCount ?? funnelStats?.spinStart ?? funnelStats?.gameStart ?? 0}
                                </div>
                            </div>
                        </div>

                        {/* 6. Giros Completados */}
                        <div style={{ flex: '0 0 160px', minWidth: '160px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    🏆 Giros Finalizados
                                </div>
                                <div className="fs-3 fw-bold text-success">
                                    {funnelStats?.spinCompleteCount ?? funnelStats?.spinComplete ?? funnelStats?.gameComplete ?? 0}
                                </div>
                            </div>
                        </div>

                        {/* 7. Cupones Guardados */}
                        <div style={{ flex: '0 0 160px', minWidth: '160px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    💾 Cupones Guardados
                                </div>
                                <div className="fs-3 fw-bold text-primary-emphasis">
                                    {funnelStats?.passDownloadCount ?? funnelStats?.voucherDownload ?? funnelStats?.couponSaved ?? 0}
                                </div>
                            </div>
                        </div>

                        {/* 8. Premios Canjeados */}
                        <div style={{ flex: '0 0 160px', minWidth: '160px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    🎁 Premios Canjeados
                                </div>
                                <div className="fs-3 fw-bold text-danger">
                                    {funnelStats?.prizeRedeemedCount ?? funnelStats?.prizeRedeemed ?? 0}
                                </div>
                            </div>
                        </div>

                        {/* KPI 1: Stock Total */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-dark text-white rounded-3 border border-secondary h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-white-50 small fw-semibold text-uppercase mb-1">
                                    📦 Stock Total
                                </div>
                                <div className="fs-3 fw-bold text-warning">
                                    {stockKPIs.total} u
                                </div>
                                <small className="text-white-50" style={{ fontSize: '0.75rem' }}>Premios físicos</small>
                            </div>
                        </div>

                        {/* KPI 2: Stock Otorgado */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-dark text-white rounded-3 border border-secondary h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-white-50 small fw-semibold text-uppercase mb-1">
                                    🎖️ Stock Otorgado
                                </div>
                                <div className="fs-3 fw-bold text-info">
                                    {stockKPIs.awarded} u
                                </div>
                                <small className="text-white-50" style={{ fontSize: '0.75rem' }}>Asignados en ruleta</small>
                            </div>
                        </div>

                        {/* KPI 3: Stock Restante */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-dark text-white rounded-3 border border-warning h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-white-50 small fw-semibold text-uppercase mb-1">
                                    🟢 Stock Restante
                                </div>
                                <div className={`fs-3 fw-bold ${stockKPIs.remaining <= 5 ? 'text-danger' : 'text-success'}`}>
                                    {stockKPIs.remaining} u
                                </div>
                                <small className="text-white-50" style={{ fontSize: '0.75rem' }}>Disponibles para ganar</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. FILTROS Y BUSCADOR */}
                <div className="card shadow-sm border-0 rounded-4 p-3 bg-white mb-4">
                    <div className="row g-2 align-items-center">
                        {/* Buscador Universal */}
                        <div className="col-12 col-md-6 col-lg-5">
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="bi bi-search text-secondary"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Buscar por Folio (#SD26-1234), Nombre, Correo, Teléfono o ZIP..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary border-start-0"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Filtro por Estatus */}
                        <div className="col-12 col-md-6 col-lg-4">
                            <div className="d-flex align-items-center gap-2">
                                <label className="small fw-semibold text-secondary text-nowrap">Filtrar Estado:</label>
                                <select
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="all">Todos los participantes</option>
                                    <option value="WINNERS">★ Ganadores de Premios</option>
                                    <option value="NON_WINNERS">Pases de Cortesía / No Ganadores</option>
                                    <option value="REDEEMED">🎁 Premios Canjeados</option>
                                    <option value="PENDING_REDEEM">⏳ Pendientes de Canje</option>
                                    <option value="ARCHIVED">Archivados / Reactivados</option>
                                </select>
                            </div>
                        </div>

                        {/* Contador */}
                        <div className="col-12 col-lg-3 text-lg-end text-muted small">
                            Total: <strong className="text-dark">{totalCount}</strong> personas
                        </div>
                    </div>
                </div>

                {/* 4. TABLA PAGINADA DE PARTICIPANTES */}
                <div className="card shadow-sm border-0 rounded-4 overflow-hidden bg-white mb-4">
                    <div className="card-header bg-white p-3 px-4 d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-people-fill text-danger fs-5"></i>
                            <h5 className="mb-0 fw-bold text-dark">
                                {statusFilter === 'WINNERS' ? '★ Listado de Ganadores Oficiales' : 'Listado de Participantes de Ruleta'}
                            </h5>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                className="btn btn-outline-success btn-sm fw-semibold d-flex align-items-center gap-2 shadow-sm"
                                onClick={handleExportExcel}
                                disabled={isExporting}
                                title="Descargar reporte completo en Excel (3 hojas)"
                            >
                                {isExporting ? (
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                    <i className="bi bi-file-earmark-excel-fill text-success"></i>
                                )}
                                <span>Exportar a Excel</span>
                            </button>
                            <span className="badge bg-light text-dark border">
                                Página {currentPage} de {totalPages || 1}
                            </span>
                        </div>
                    </div>

                    <div className="table-responsive" style={{ minHeight: '320px' }}>
                        <table className="table align-middle table-hover mb-0">
                            <thead className="table-light">
                                <tr className="small text-secondary fw-bold text-uppercase">
                                    <th scope="col" className="text-center" style={{ width: '60px' }}>#</th>
                                    <th scope="col" className="cursor-pointer user-select-none" style={{ width: '130px' }} onClick={() => handleSort('passCode')}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Folio</span>
                                            <i className={`bi ${sortField === 'passCode' ? (sortDirection === 'asc' ? 'bi-sort-up text-danger' : 'bi-sort-down text-danger') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col" className="cursor-pointer user-select-none" onClick={() => handleSort('fullName')}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Aficionado</span>
                                            <i className={`bi ${sortField === 'fullName' ? (sortDirection === 'asc' ? 'bi-sort-up text-danger' : 'bi-sort-down text-danger') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col">Contacto</th>
                                    <th scope="col" className="cursor-pointer user-select-none" onClick={() => handleSort('prize')}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Premio / Resultado</span>
                                            <i className={`bi ${sortField === 'prize' ? (sortDirection === 'asc' ? 'bi-sort-up text-danger' : 'bi-sort-down text-danger') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col" className="cursor-pointer user-select-none" onClick={() => handleSort('createdAt')}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Fecha</span>
                                            <i className={`bi ${sortField === 'createdAt' ? (sortDirection === 'asc' ? 'bi-sort-up text-danger' : 'bi-sort-down text-danger') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col" className="text-center" style={{ width: '180px' }}>
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <div className="spinner-border text-danger" role="status">
                                                <span className="visually-hidden">Cargando...</span>
                                            </div>
                                            <p className="text-secondary small mt-2 mb-0">Cargando participantes...</p>
                                        </td>
                                    </tr>
                                ) : sortedParticipants.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5 text-secondary">
                                            <i className="bi bi-inbox fs-1 d-block text-muted mb-2"></i>
                                            <p className="fs-6 fw-bold mb-1">No se encontraron registros</p>
                                            <p className="small text-muted mb-0">Intenta cambiar los términos de búsqueda o filtros aplicados.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    sortedParticipants.map((p, index) => {
                                        const globalIndex = (currentPage - 1) * pageSize + index + 1;
                                        const isWinner = p.isWinner ?? (p.prize?.isWinner ?? false);
                                        const hasRedeemed = p.hasRedeemedPrize || p.isRedeemed || p.status === 'REDEEMED';
                                        const prizeTitle = p.prizeTitleSnapshot || p.prizeText || p.prize?.textEs || (isWinner ? 'Premio Oficial' : 'Sigue participando');

                                        return (
                                            <tr key={p.id || index} className={p.isArchived ? 'opacity-50' : isWinner ? 'fw-medium' : ''}>
                                                {/* Posición */}
                                                <td className="text-center text-muted fw-bold">
                                                    #{globalIndex}
                                                </td>

                                                {/* Folio */}
                                                <td>
                                                    <span className="badge bg-dark font-monospace px-2 py-1 fs-6">
                                                        {p.passCode || p.participationTicket || p.folio || '-'}
                                                    </span>
                                                </td>

                                                {/* Nombre */}
                                                <td>
                                                    <div className="fw-bold text-dark">{p.fullName || 'Aficionado'}</div>
                                                    {p.attemptNumber > 1 && (
                                                        <span className="badge bg-info text-dark rounded-pill" style={{ fontSize: '0.65rem' }}>
                                                            Reintento #{p.attemptNumber}
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Contacto */}
                                                <td>
                                                    <div className="small text-secondary text-truncate" style={{ maxWidth: '180px' }}>
                                                        <i className="bi bi-envelope me-1"></i>{p.email || 'N/A'}
                                                    </div>
                                                    <div className="small text-secondary">
                                                        <i className="bi bi-telephone me-1"></i>{p.phone || 'N/A'}
                                                        {p.zipCode && <span className="ms-1.5 badge bg-light text-muted border">ZIP: {p.zipCode}</span>}
                                                    </div>
                                                </td>

                                                {/* Premio / Resultado */}
                                                <td>
                                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                                        <div className="d-flex align-items-center gap-1.5">
                                                            <i className={`bi ${isWinner ? 'bi-trophy-fill text-warning' : 'bi-ticket-fill text-secondary'}`}></i>
                                                            <span className={isWinner ? 'fw-bold text-dark' : 'text-muted'}>
                                                                {prizeTitle}
                                                            </span>
                                                        </div>
                                                        {isWinner && !hasRedeemed && !p.isArchived && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1 py-0.5 px-2 fw-bold shadow-sm rounded-pill"
                                                                style={{ fontSize: '0.72rem' }}
                                                                title="Canjear premio en sitio"
                                                                onClick={() => {
                                                                    setInitialRedeemCode(p.passCode || p.participationTicket || p.folio || '');
                                                                    setIsRedeemModalOpen(true);
                                                                }}
                                                            >
                                                                <i className="bi bi-gift-fill"></i>
                                                                <span>Canjear</span>
                                                            </button>
                                                        )}
                                                        {hasRedeemed && (
                                                            <span className="badge bg-success-subtle text-success border border-success" style={{ fontSize: '0.72rem' }}>
                                                                Canjeado
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Fecha */}
                                                <td className="small text-muted text-nowrap">
                                                    {formatEventDate(p.playedAt || p.createdAt)}
                                                </td>

                                                {/* Acciones */}
                                                <td className="text-center">
                                                    {p.isArchived ? (
                                                        <span
                                                            className="badge bg-light text-muted border d-inline-flex align-items-center gap-1"
                                                            title={p.restitutionReason ? `Restituido: ${p.restitutionReason}` : 'Participante archivado'}
                                                            style={{ cursor: 'help' }}
                                                        >
                                                            <i className="bi bi-archive"></i>
                                                            <span>Restituido</span>
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-warning text-dark d-inline-flex align-items-center gap-1 py-1 px-2.5 fw-semibold shadow-sm rounded-pill"
                                                            style={{ fontSize: '0.75rem' }}
                                                            title="Dar otra oportunidad (Archivar intento y permitir nuevo registro)"
                                                            onClick={() => {
                                                                setSelectedParticipantForReset(p);
                                                                setIsResetModalOpen(true);
                                                            }}
                                                        >
                                                            <i className="bi bi-arrow-counterclockwise"></i>
                                                            <span>Dar otra oportunidad</span>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINACIÓN */}
                    <div className="card-footer bg-light p-3 d-flex flex-wrap justify-content-between align-items-center gap-2 border-top">
                        <div className="small text-secondary">
                            Mostrando {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalCount)} de {totalCount} participantes
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '85px' }}
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="10">10 / pág</option>
                                <option value="25">25 / pág</option>
                                <option value="50">50 / pág</option>
                                <option value="100">100 / pág</option>
                            </select>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage <= 1 || isLoading}
                                onClick={() => setCurrentPage(1)}
                                title="Primera página"
                            >
                                <i className="bi bi-chevron-double-left"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage <= 1 || isLoading}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                title="Página anterior"
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>

                            <span className="small fw-semibold text-dark px-2">
                                Página {currentPage} de {totalPages || 1}
                            </span>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage >= totalPages || isLoading}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                title="Página siguiente"
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage >= totalPages || isLoading}
                                onClick={() => setCurrentPage(totalPages)}
                                title="Última página"
                            >
                                <i className="bi bi-chevron-double-right"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 5. SECCIÓN DESPLEGABLE: CONTROL DE REGISTROS */}
                <div className="card shadow-sm border-0 rounded-4 overflow-hidden mb-3 bg-white">
                    <div
                        className="card-header bg-white p-3 px-4 d-flex justify-content-between align-items-center cursor-pointer user-select-none"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsParticipationSectionOpen(prev => !prev)}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-sliders text-danger fs-5"></i>
                            <div>
                                <h6 className="mb-0 fw-bold text-dark">Control de Registros y Participación</h6>
                                <small className="text-secondary">Pausar o abrir el registro en la ruleta en tiempo real</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span className={`badge ${isDynamicCurrentlyActive ? 'bg-success-subtle text-success border border-success' : 'bg-danger-subtle text-danger border border-danger'}`}>
                                {isDynamicCurrentlyActive ? 'Registros Abiertos' : 'Registros Pausados'}
                            </span>
                            <i className={`bi ${isParticipationSectionOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-secondary fs-6`}></i>
                        </div>
                    </div>

                    {isParticipationSectionOpen && (
                        <div className="card-body p-4 border-top bg-light-subtle">
                            <p className="text-secondary small mb-3">
                                Estatus actual: <span className={`fw-bold ${isDynamicCurrentlyActive ? 'text-success' : 'text-danger'}`}>
                                    {isDynamicCurrentlyActive ? 'Dinámica Activa (Recibiendo registros y giros de aficionados)' : 'Dinámica Pausada (Acceso bloqueado en pantalla de cortesía)'}
                                </span>
                            </p>
                            <button
                                type="button"
                                onClick={() => triggerToggleActive(!isDynamicCurrentlyActive)}
                                className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                    isDynamicCurrentlyActive ? 'btn-outline-danger' : 'btn-outline-success'
                                }`}
                            >
                                <i className={`bi ${isDynamicCurrentlyActive ? 'bi-pause-circle-fill' : 'bi-play-circle-fill'}`}></i>
                                {isDynamicCurrentlyActive ? 'Pausar Ruleta (Bloquear nuevos registros)' : 'Activar Ruleta (Abrir registros a aficionados)'}
                            </button>
                        </div>
                    )}
                </div>

                {/* 6. SECCIÓN DESPLEGABLE: ZONA PELIGROSA */}
                <div className="card shadow-sm border border-danger border-opacity-25 rounded-4 overflow-hidden mb-4 bg-white">
                    <div
                        className="card-header bg-danger-subtle p-3 px-4 d-flex justify-content-between align-items-center cursor-pointer user-select-none"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsDangerZoneSectionOpen(prev => !prev)}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill text-danger fs-5"></i>
                            <div>
                                <h6 className="mb-0 fw-bold text-danger">Zona Peligrosa / Mantenimiento</h6>
                                <small className="text-danger-emphasis">Reinicio total y eliminación definitiva de registros</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span className="badge bg-danger text-white">Acción Destructiva</span>
                            <i className={`bi ${isDangerZoneSectionOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-danger fs-6`}></i>
                        </div>
                    </div>

                    {isDangerZoneSectionOpen && (
                        <div className="card-body p-4 border-top bg-white">
                            <p className="small text-danger-emphasis mb-3">
                                Reiniciar la ruleta eliminará permanentemente todos los participantes registrados, folios, cupones generados, stock asignado y telemetría de esta sala de forma irreversible.
                            </p>
                            <button
                                type="button"
                                onClick={handleStartResetFlow}
                                className="btn btn-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2"
                            >
                                <i className="bi bi-trash-fill"></i>
                                Reiniciar Ruleta Baja Cup por completo
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL DE INSTRUCCIONES Y CÓDIGO QR */}
            <PersonalRouletteInstructionsModal
                isOpen={isInstructionsModalOpen}
                onClose={() => setIsInstructionsModalOpen(false)}
                roomId={roomId}
                config={config}
                room={room}
            />

            {/* MODAL DE CONFIGURACIÓN (4 TABS) */}
            <PersonalRouletteConfigModal
                isOpen={isConfigModalOpen}
                onClose={() => setIsConfigModalOpen(false)}
                roomId={roomId}
                onConfigUpdated={() => loadData(false)}
            />

            {/* MODAL DE CANJE Y ENTREGA DE PREMIOS EN SITIO (HU-05) */}
            <PersonalRouletteRedeemModal
                isOpen={isRedeemModalOpen}
                onClose={() => {
                    setIsRedeemModalOpen(false);
                    setInitialRedeemCode('');
                }}
                roomId={roomId}
                initialCode={initialRedeemCode}
                onRedeemSuccess={() => {
                    loadData(false);
                }}
            />

            {/* MODAL DE SOPORTE: RESTITUCIÓN DE OPORTUNIDAD (HU-06) */}
            <PersonalRouletteResetModal
                isOpen={isResetModalOpen}
                onClose={() => {
                    setIsResetModalOpen(false);
                    setSelectedParticipantForReset(null);
                }}
                participant={selectedParticipantForReset}
                roomId={roomId}
                onResetSuccess={() => {
                    loadData(false);
                }}
            />

            {/* MODAL DE EDICIÓN EN CALIENTE DE LA FILA DE PREMIOS (HU-07) */}
            <PersonalRouletteQueueModal
                isOpen={isQueueModalOpen}
                onClose={() => setIsQueueModalOpen(false)}
                roomId={roomId}
                config={config}
                onQueueUpdated={() => loadData(false)}
            />

            {/* MODAL DE SEGURIDAD (CONTRASEÑA PARA RESET) */}
            {isPasswordModalOpen && (
                <>
                    <div
                        onClick={() => {
                            setIsPasswordModalOpen(false);
                            setPasswordInput('');
                            setPasswordError('');
                        }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                            zIndex: 110000,
                            backdropFilter: 'blur(4px)'
                        }}
                    />
                    <div
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '95%',
                            maxWidth: '440px',
                            zIndex: 110001
                        }}
                    >
                        <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white">
                            <div className="bg-danger p-3 px-4 d-flex justify-content-between align-items-center text-white">
                                <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                    <i className="bi bi-shield-lock-fill"></i>
                                    <span>Clave de Autorización</span>
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setIsPasswordModalOpen(false);
                                        setPasswordInput('');
                                        setPasswordError('');
                                    }}
                                ></button>
                            </div>

                            <form onSubmit={handleValidatePasswordSubmit}>
                                <div className="card-body p-4">
                                    <p className="small text-secondary mb-3">
                                        Esta acción borrará permanentemente todos los datos de la sala. Ingresa la contraseña de autorización para continuar:
                                    </p>
                                    <div className="mb-3">
                                        <label className="form-label small fw-semibold text-dark">
                                            Contraseña de seguridad:
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                                            placeholder="Ingresa la contraseña..."
                                            value={passwordInput}
                                            onChange={(e) => {
                                                setPasswordInput(e.target.value);
                                                if (passwordError) setPasswordError('');
                                            }}
                                            autoFocus
                                        />
                                        {passwordError && (
                                            <div className="invalid-feedback d-block mt-2 fw-semibold">
                                                {passwordError}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="card-footer bg-light p-3 px-4 d-flex justify-content-end gap-2 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-3"
                                        onClick={() => {
                                            setIsPasswordModalOpen(false);
                                            setPasswordInput('');
                                            setPasswordError('');
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-danger px-4 fw-bold text-white d-flex align-items-center gap-2"
                                    >
                                        <i className="bi bi-key-fill"></i>
                                        <span>Validar</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </>
            )}

            {/* MODAL DE CONFIRMACIÓN REUTILIZABLE */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                    setConfirmData={setConfirmData}
                />
            )}
        </SecondaryBase>
    );
};

export default PersonalRouletteBoard;
